/** 현장 안전 카카오 알림톡 — 시스템 설정 UI / API */

export type SafetyKakaoProvider = 'auto' | 'log' | 'webhook' | 'aligo';
export type SafetyKakaoWebhookFormat = 'generic' | 'aligo_alimtalk';

export interface SafetyKakaoSettings {
  enabled: boolean;
  default_selected: boolean;
  provider: SafetyKakaoProvider;
  use_sms_aligo_credentials: boolean;
  aligo_api_url: string;
  aligo_api_key: string;
  aligo_user_id: string;
  aligo_sender_key: string;
  aligo_sender: string;
  aligo_test_mode: boolean;
  tpl_code_warning: string;
  tpl_code_danger: string;
  subject_template: string;
  webhook_url: string;
  webhook_format: SafetyKakaoWebhookFormat;
  webhook_batch: boolean;
  timeout_sec: number;
}

export const DEFAULT_SAFETY_KAKAO_SETTINGS: SafetyKakaoSettings = {
  enabled: false,
  default_selected: true,
  provider: 'auto',
  use_sms_aligo_credentials: true,
  aligo_api_url: 'https://apis.aligo.in/akv10/alimtalk/send/',
  aligo_api_key: '',
  aligo_user_id: '',
  aligo_sender_key: '',
  aligo_sender: '',
  aligo_test_mode: true,
  tpl_code_warning: '',
  tpl_code_danger: '',
  subject_template: '시설 안전알림',
  webhook_url: '',
  webhook_format: 'generic',
  webhook_batch: false,
  timeout_sec: 12,
};

export function mergeSafetyKakaoSettings(
  partial?: Partial<SafetyKakaoSettings> | null
): SafetyKakaoSettings {
  const p = partial || {};
  const d = DEFAULT_SAFETY_KAKAO_SETTINGS;
  const prov = String(p.provider || d.provider).toLowerCase();
  const provider: SafetyKakaoProvider =
    prov === 'log' || prov === 'webhook' || prov === 'aligo' ? prov : 'auto';
  const wf = String(p.webhook_format || d.webhook_format).toLowerCase();
  const webhook_format: SafetyKakaoWebhookFormat =
    wf === 'aligo_alimtalk' ? 'aligo_alimtalk' : 'generic';
  const bool = (v: unknown, fallback: boolean) => {
    if (typeof v === 'boolean') return v;
    if (v === 'true' || v === '1') return true;
    if (v === 'false' || v === '0') return false;
    return fallback;
  };
  const num = (v: unknown, fallback: number) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : fallback;
  };
  return {
    enabled: bool(p.enabled, d.enabled),
    default_selected: bool(p.default_selected, d.default_selected),
    provider,
    use_sms_aligo_credentials: bool(p.use_sms_aligo_credentials, d.use_sms_aligo_credentials),
    aligo_api_url:
      String(p.aligo_api_url ?? d.aligo_api_url).trim() || d.aligo_api_url,
    aligo_api_key: String(p.aligo_api_key ?? d.aligo_api_key),
    aligo_user_id: String(p.aligo_user_id ?? d.aligo_user_id).trim(),
    aligo_sender_key: String(p.aligo_sender_key ?? d.aligo_sender_key).trim(),
    aligo_sender: String(p.aligo_sender ?? d.aligo_sender).trim(),
    aligo_test_mode: bool(p.aligo_test_mode, d.aligo_test_mode),
    tpl_code_warning: String(p.tpl_code_warning ?? d.tpl_code_warning).trim(),
    tpl_code_danger: String(p.tpl_code_danger ?? d.tpl_code_danger).trim(),
    subject_template:
      String(p.subject_template ?? d.subject_template).trim() || d.subject_template,
    webhook_url: String(p.webhook_url ?? d.webhook_url).trim(),
    webhook_format,
    webhook_batch: bool(p.webhook_batch, d.webhook_batch),
    timeout_sec: Math.min(60, Math.max(3, Math.floor(num(p.timeout_sec, d.timeout_sec)))),
  };
}
